import { filter, find } from 'lodash/fp';
import { createSelector } from 'reselect';
import { array, ord, string } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { createAction } from '@mews/framework';
import { getStringProp } from '@mews/fp';
import { ServiceOrderNoteType } from '@mews-commander/data-access';
import { ApplicationState, ServiceOrderNote } from '@mews-commander/types';
import parentSelector from '../selector';
import prefix from '../prefix';
import { createBaseEntitySelectors, createEntitiesReducer, createEntityPropertyIncludesSelector } from './utils';

export const NAME = 'serviceOrderNotes';

export const SERVICE_ORDER_NOTES_LOADED = prefix('SERVICE_ORDER_NOTES_LOADED');

const SERVICE_ORDER_NOTES_DELETED = prefix('SERVICE_ORDER_NOTES_DELETED');

export const setServiceOrderNotes = (serviceOrderNotes: ServiceOrderNote[]) =>
    createAction(SERVICE_ORDER_NOTES_LOADED, { entities: serviceOrderNotes });

export const deleteServiceOrderNotes = (noteIds: string[]) => createAction(SERVICE_ORDER_NOTES_DELETED, noteIds);

export const serviceOrderNotesReducer = createEntitiesReducer({
    FETCH_SUCCESS: SERVICE_ORDER_NOTES_LOADED,
    DELETE_SUCCESS: SERVICE_ORDER_NOTES_DELETED,
});

export const [getServiceOrderNotes, getServiceOrderNotesByIds, getServiceOrderNotesByOwnerId, getServiceOrderNote] =
    createBaseEntitySelectors<ApplicationState, ServiceOrderNote, typeof NAME>(parentSelector, NAME);

export const getGeneralServiceOrderNoteByServiceOrderId = createSelector(
    getStringProp('serviceOrderId'),
    getServiceOrderNotes,
    (serviceOrderId, notes) =>
        find(
            (serviceOrderNote) =>
                serviceOrderNote.serviceOrderId === serviceOrderId &&
                serviceOrderNote.type === ServiceOrderNoteType.General,
            notes
        )
);

export const getNotesByServiceOrderId = createSelector(
    getStringProp('serviceOrderId'),
    getServiceOrderNotes,
    (serviceOrderId, notes) => filter({ serviceOrderId }, notes)
);
export const getNotesByServiceOrderIds = createEntityPropertyIncludesSelector(
    getServiceOrderNotes,
    'serviceOrderId',
    'serviceOrderIds'
);
const byType = pipe(
    string.Ord,
    ord.contramap(({ type }: ServiceOrderNote) => type)
);
const byCreatedUtc = pipe(
    string.Ord,
    ord.contramap(({ createdUtc }: ServiceOrderNote) => createdUtc)
);
export const orderServiceOrderNotes = array.sortBy([ord.reverse(byType), ord.reverse(byCreatedUtc)]);

export const getNotesSortedByServiceOrderId = flow(getNotesByServiceOrderId, orderServiceOrderNotes);

export const getNoteIdsSortedByServiceOrderId = flow(
    getNotesSortedByServiceOrderId,
    array.map(({ id }) => id)
);
